import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { FaInbox } from '@react-icons/all-files/fa/FaInbox';
import { FaSync } from '@react-icons/all-files/fa/FaSync';
import { FaChevronLeft } from '@react-icons/all-files/fa/FaChevronLeft';
import { FaChevronRight } from '@react-icons/all-files/fa/FaChevronRight';
import { FaChevronDown } from '@react-icons/all-files/fa/FaChevronDown';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaPaperclip } from '@react-icons/all-files/fa/FaPaperclip';
import { FaDownload } from '@react-icons/all-files/fa/FaDownload';
import { FaExternalLinkAlt } from '@react-icons/all-files/fa/FaExternalLinkAlt';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaUser } from '@react-icons/all-files/fa/FaUser';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaExclamationCircle } from '@react-icons/all-files/fa/FaExclamationCircle';
import { FaSort } from '@react-icons/all-files/fa/FaSort';
import { FaSortDown } from '@react-icons/all-files/fa/FaSortDown';
import { FaSortUp } from '@react-icons/all-files/fa/FaSortUp';
import ProfaseModal from '../commons/ProfaseModal';
import { TabPanel, TabPanelContainer } from '../commons/TabPanel';
import {
  consultarNotice,
  healthDotColor,
  healthStatusOf,
  healthStatusTone,
  isConsultaStale,
  type HealthDotTone,
} from './buzon-health-status';
import PdfViewerModal from '../explorer/PdfViewerModal';
import TxtViewerModal from '../explorer/TxtViewerModal';
import ImageViewerModal from '../explorer/ImageViewerModal';
import { isImageFile, isPdfFile, isTxtFile } from '../explorer/archivo-file-types';
import { normalizePdfBytes } from '../explorer/pdf-bytes';
import type { IProfaseArchivo } from '../explorer/profase-archivo-api';
import axios from 'axios';
import BuzonTributarioUsuariosConfig from './BuzonTributarioUsuariosConfig';
import { CerradoPor } from './CerradoPor';
import {
  formatBuzonFecha,
  formatBuzonHora,
  isBuzonFallosView,
  normalizeBuzonAsunto,
  normalizeBuzonRfc,
  pagerPageIndexes,
  parseBuzonPage,
  parseBuzonVista,
  sortQuery,
  toApiPage,
  type BuzonVista,
  type SortDir,
} from './buzon-list-helpers';
import flagMexico from '../assets/flags/Flag_of_Mexico.svg';

const PAGE_SIZE = 20;
const API_BASE = '/api/buzon-tributario/items';
const FALLOS_API = '/api/buzon-tributario/fallos';
const HEALTH_API = '/api/buzon-tributario/health';
const CONSULTAR_API = '/api/buzon-tributario/consultar';
const SINCRONIZAR_API = '/api/buzon-tributario/sincronizar';
const HEALTH_POLL_MS = 60_000;
const ATTACHMENT_VIEWER_Z = 110000;
const VIEW_ASIGNADOS_A_MI = 'asignados-a-mi';
const VIEW_SIN_ASIGNAR = 'sin-asignar';
const VIEW_FALLOS = 'fallos';
const VIEW_FALLOS_NUEVOS = 'fallos-nuevos';
const VIEW_FALLOS_DESCARTADOS = 'fallos-descartados';

function buzonQueryString(
  searchParams: URLSearchParams,
  view: string,
  options?: { dropEmpresa?: boolean; dropAsunto?: boolean; page?: number; vista?: BuzonVista },
): string {
  const p = new URLSearchParams();
  const vista = options?.vista ?? parseBuzonVista(searchParams.get('vista'), view);
  if (vista === 'fallos') p.set('vista', 'fallos');
  if (view) p.set('view', view);
  if (!options?.dropEmpresa) {
    const empresaId = searchParams.get('empresaId');
    const empresa = searchParams.get('empresa');
    const rfc = searchParams.get('rfc');
    if (empresaId) p.set('empresaId', empresaId);
    if (empresa) p.set('empresa', empresa);
    if (rfc && !empresaId) p.set('rfc', rfc);
  }
  if (vista !== 'fallos' && !options?.dropAsunto) {
    const asunto = normalizeBuzonAsunto(searchParams.get('asunto'));
    if (asunto) p.set('asunto', asunto);
  }
  const page = options?.page ?? parseBuzonPage(searchParams.get('page'));
  p.set('page', String(page));
  return p.toString();
}

function buzonPath(
  searchParams: URLSearchParams,
  view: string,
  options?: { id?: number; dropEmpresa?: boolean; page?: number; vista?: BuzonVista },
): string {
  const qs = buzonQueryString(searchParams, view, options);
  const base = options?.id != null ? `/buzon-tributario/${options.id}` : '/buzon-tributario';
  return qs ? `${base}?${qs}` : base;
}

const ESTADOS = ['NUEVO', 'LEIDO', 'PENDIENTE', 'CERRADO'] as const;
type EstadoBuzon = (typeof ESTADOS)[number];

interface BuzonAttachment {
  id: number;
  nombre: string;
  path?: string;
  contentType?: string | null;
}

interface BuzonItemData {
  body?: string | null;
  subject?: string | null;
  correo?: string | null;
  folio?: string | null;
  razonSocial?: string | null;
  fechaConsulta?: string | null;
  ObservacionesYSeguimiento?: string | null;
  observacionesYSeguimiento?: string | null;
}

interface BuzonFallo {
  id: number;
  persistentId?: string | null;
  subject?: string | null;
  rfc?: string | null;
  motivoError: string;
  estado?: string | null;
  creado?: string | null;
}

interface BuzonConcepto {
  id?: number;
  codigo?: string;
  nombre?: string;
}

interface BuzonItem {
  id: number;
  rfc: string;
  fecha: string;
  hora: string;
  asunto?: string | BuzonConcepto | null;
  concepto?: string | BuzonConcepto | null;
  mensajeId?: string;
  empresaId?: number | null;
  grupoId?: number | null;
  estado: string;
  leidoPor?: number | null;
  leido?: string | null;
  cerrado?: string | null;
  cerradoPor?: number | null;
  asignadoA?: number | null;
  data?: BuzonItemData | null;
  attachments?: BuzonAttachment[] | null;
}

interface EstadoIndicador {
  estado: string;
  cantidad: number;
  porcentaje?: number;
}

interface AsignadoIndicador {
  usuarioId?: number | null;
  nombre?: string | null;
  cantidad: number;
  porcentaje?: number;
}

interface ConceptoIndicador {
  codigo?: string;
  nombre?: string;
  cantidad: number;
  porcentaje?: number;
}

interface Indicadores {
  estados?: EstadoIndicador[];
  asuntos?: ConceptoIndicador[];
  asignadosA?: AsignadoIndicador[];
  asignadosAMi?: AsignadoIndicador | null;
  sinAsignar?: AsignadoIndicador | null;
}

interface BuzonHealthSync {
  id?: number | null;
  ultimaConsulta?: string | null;
  ultimaDescarga?: string | null;
  cantidadItemsDescargados?: number | null;
  sincronizar?: boolean;
}

interface BuzonHealthProbe {
  ok?: boolean;
  error?: string | null;
  host?: string | null;
  country?: string | null;
  configured?: boolean;
  reachable_via?: string | null;
  captcha_api_key_configured?: boolean;
}

interface BuzonHealth {
  ok?: boolean;
  status?: string | null;
  error?: string | null;
  service?: string | null;
  ultimaConsulta?: string | null;
  ultimaDescarga?: string | null;
  cantidadItemsDescargados?: number | null;
  sincronizar?: boolean;
  sync?: BuzonHealthSync | null;
  outlook?: BuzonHealthProbe | null;
  backend?: BuzonHealthProbe | null;
  vpn?: BuzonHealthProbe | null;
  captcha?: BuzonHealthProbe | null;
  profase_crm?: BuzonHealthProbe | null;
}

interface UsuarioSearchItem {
  id: number;
  nombreCompleto?: string | null;
  nombre?: string | null;
  apellido?: string | null;
  usuario?: string | null;
  email?: string | null;
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function attachmentPreviewKind(att: BuzonAttachment): 'pdf' | 'txt' | 'image' | 'other' {
  const nombre = att.nombre || '';
  const ct = (att.contentType ?? '').toLowerCase();
  if (isPdfFile(nombre) || ct.includes('pdf')) return 'pdf';
  if (isImageFile(nombre) || ct.startsWith('image/')) return 'image';
  if (isTxtFile(nombre) || ct.includes('text/plain') || ct.includes('text/html')) return 'txt';
  return 'other';
}

async function fetchAttachmentBlob(itemId: number, attachment: BuzonAttachment): Promise<Blob> {
  const res = await axios.get<ArrayBuffer>(
    `${API_BASE}/${itemId}/attachments/${attachment.id}/contenido`,
    { responseType: 'arraybuffer', withCredentials: true },
  );
  const buffer = res.data;
  if (!buffer || buffer.byteLength < 4) {
    throw new Error('No se pudo descargar el adjunto');
  }
  const nombre = (attachment.nombre || '').toLowerCase();
  const type = (attachment.contentType ?? '').toLowerCase();
  if (nombre.endsWith('.pdf') || type.includes('pdf')) {
    const pdf = normalizePdfBytes(buffer);
    return new Blob([pdf], { type: 'application/pdf' });
  }
  return new Blob([buffer], {
    type: attachment.contentType || 'application/octet-stream',
  });
}

function fmtFechaHora(fecha?: string | null, hora?: string | null): string {
  const datePart = formatBuzonFecha(fecha);
  const timePart = formatBuzonHora(hora);
  return [datePart, timePart].filter(Boolean).join(' ');
}

function fmtInstantFechaHora(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

function healthSyncOf(health: BuzonHealth | null): BuzonHealthSync {
  return health?.sync ?? {
    ultimaConsulta: health?.ultimaConsulta,
    ultimaDescarga: health?.ultimaDescarga,
    cantidadItemsDescargados: health?.cantidadItemsDescargados,
  };
}

function fmtHace(value?: string | null): string {
  if (!value) return 'sin datos';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return fmtInstantFechaHora(value);
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return 'hace instantes';
  const min = Math.floor(sec / 60);
  if (min < 60) return `hace ${Math.max(1, min)} min`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `hace ${hours} h`;
  return fmtInstantFechaHora(value);
}

const HEALTH_FIELD_LABELS: Record<string, string> = {
  ok: 'OK',
  status: 'Estado',
  service: 'Servicio',
  error: 'Error',
  id: 'ID',
  ultimaConsulta: 'Última consulta',
  ultimaDescarga: 'Última descarga',
  cantidadItemsDescargados: 'Items descargados',
  host: 'Host',
  country: 'País',
  configured: 'Configurado',
  reachable_via: 'Acceso vía',
  captcha_api_key_configured: 'Captcha configurado',
};

const HEALTH_KNOWN_KEYS = new Set([
  'ok', 'status', 'service', 'error', 'sync',
  'outlook', 'backend', 'vpn', 'captcha', 'profase_crm',
  'ultimaConsulta', 'ultimaDescarga', 'cantidadItemsDescargados', 'sincronizar',
]);

function isMexicoCountry(value: unknown): boolean {
  const normalized = String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
  return normalized === 'MX' || normalized === 'MEX' || normalized === 'MEXICO';
}

function healthFieldLabel(key: string): string {
  return HEALTH_FIELD_LABELS[key] || key;
}

function healthFieldValue(key: string, value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (key === 'ultimaConsulta' || key === 'ultimaDescarga' || /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const formatted = fmtInstantFechaHora(value);
      if (!formatted) return value;
      return `${formatted} (${fmtHace(value)})`;
    }
    return value;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function healthObjectRows(obj: object | null | undefined): { key: string; label: string; value: string; raw?: unknown }[] {
  if (!obj) return [{ key: 'estado', label: 'Estado', value: 'sin datos' }];
  return Object.entries(obj).map(([key, value]) => ({
    key,
    label: healthFieldLabel(key),
    value: healthFieldValue(key, value),
    raw: value,
  }));
}

function empresaNombre(item: BuzonItem): string {
  return item.data?.razonSocial?.trim() || '';
}

function EmpresaNoRegistradaWarning() {
  return (
    <span style={s.empresaWarning}>
      <FaExclamationCircle style={{ flexShrink: 0, fontSize: 11 }} />
      Empresa no registrada
    </span>
  );
}

function grupoNombreFromPayload(g: { alias?: string | null; grpAliasDecomsa?: string | null; grpAliasEquilibrio?: string | null }): string {
  return (g.alias || g.grpAliasDecomsa || g.grpAliasEquilibrio || '').trim();
}

function isNuevo(estado?: string | null): boolean {
  return estado === 'NUEVO';
}

function usuarioDisplayName(u: UsuarioSearchItem): string {
  const full = u.nombreCompleto?.trim();
  if (full) return full;
  const parts = `${u.nombre ?? ''} ${u.apellido ?? ''}`.trim();
  return parts || u.usuario?.trim() || u.email?.trim() || `#${u.id}`;
}

function parseUsuarios(data: unknown): UsuarioSearchItem[] {
  const rows = Array.isArray(data)
    ? data
    : (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)
      ? (data as { data: unknown[] }).data
      : []);
  return rows.filter((u): u is UsuarioSearchItem =>
    !!u && typeof u === 'object' && typeof (u as UsuarioSearchItem).id === 'number'
  );
}

function buzonCatalogoLabel(valor?: string | BuzonConcepto | null): string {
  if (valor == null || valor === '') return '';
  if (typeof valor === 'string') return valor;
  return valor.nombre || valor.codigo || '';
}

function buzonConceptoLabel(concepto?: string | BuzonConcepto | null): string {
  return buzonCatalogoLabel(concepto);
}

function buzonAsuntoLabel(asunto?: string | BuzonConcepto | null): string {
  return buzonCatalogoLabel(asunto);
}

function estadoFiltroLabel(estado: string): string {
  switch (estado) {
    case 'NUEVO':
      return 'Nuevos';
    case 'LEIDO':
      return 'Leídos';
    case 'PENDIENTE':
      return 'Pendientes';
    case 'CERRADO':
      return 'Cerrados';
    default:
      return estado;
  }
}

function viewChipLabel(view: string): string {
  if (view === VIEW_ASIGNADOS_A_MI) return 'Asignados a mi';
  if (view === VIEW_SIN_ASIGNAR) return 'Sin asignar';
  if (view === VIEW_FALLOS) return 'Total de fallos';
  if (view === VIEW_FALLOS_NUEVOS) return 'Nuevos fallos';
  if (view === VIEW_FALLOS_DESCARTADOS) return 'Fallos descartados';
  return `Estado: ${estadoFiltroLabel(view)}`;
}

function isFallosView(view: string | null | undefined): boolean {
  return isBuzonFallosView(view);
}

function falloEstadoLabel(estado?: string | null): string {
  if (estado === 'NUEVO') return 'Nuevo';
  if (estado === 'DESCARTADO') return 'Descartado';
  return 'Leído';
}

function falloEstadoColors(estado?: string | null): { bg: string; color: string } {
  if (estado === 'NUEVO') return { bg: '#fee2e2', color: '#dc2626' };
  if (estado === 'DESCARTADO') return { bg: '#f1f5f9', color: '#64748b' };
  return { bg: '#e0f2fe', color: '#0369a1' };
}

function estadoLabel(estado: string): string {
  switch (estado) {
    case 'NUEVO':
      return 'Nuevo';
    case 'LEIDO':
      return 'Leído';
    case 'PENDIENTE':
      return 'Pendiente';
    case 'CERRADO':
      return 'Cerrado';
    default:
      return estado;
  }
}

function estadoBadgeColors(estado: string): { bg: string; color: string } {
  switch (estado) {
    case 'NUEVO':
      return { bg: '#dcfce7', color: '#16a34a' };
    case 'LEIDO':
      return { bg: '#e0f2fe', color: '#38bdf8' };
    case 'PENDIENTE':
      return { bg: '#fef3c7', color: '#92400e' };
    case 'CERRADO':
      return { bg: '#f1f5f9', color: '#64748b' };
    default:
      return { bg: '#f1f5f9', color: '#475569' };
  }
}

function looksLikeHtml(value?: string | null): boolean {
  if (!value) return false;
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

const MAILBOX_DROP_TAGS = 'script,iframe,object,embed,applet,frame,frameset,link,meta,base,noscript';

const MAILBOX_SHADOW_RESET =
  ':host{display:block;overflow:visible;}'
  + '.buzon-html-root{margin:0;padding:0;background:#fff;color:#202124;color-scheme:light;height:auto!important;overflow:visible!important;}'
  + '.buzon-html-root,.buzon-html-root *{max-height:none!important;overflow:visible!important;}'
  + '.buzon-html-root img{max-width:100%;height:auto;}';

function sanitizeMailboxTree(root: ParentNode) {
  root.querySelectorAll(MAILBOX_DROP_TAGS).forEach(el => el.remove());
  root.querySelectorAll('*').forEach(node => {
    const el = node as HTMLElement;
    Array.from(el.attributes).forEach(attr => {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }
      if ((name === 'href' || name === 'src' || name === 'xlink:href')
        && /^(javascript|vbscript):/i.test(value)) {
        el.removeAttribute(attr.name);
        return;
      }
      if ((name === 'href' || name === 'src') && /^data:/i.test(value) && !/^data:image\//i.test(value)) {
        el.removeAttribute(attr.name);
      }
    });
    if (el.tagName === 'A') {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    }
    if (el.tagName === 'FORM') {
      el.removeAttribute('action');
      el.setAttribute('action', '#');
      el.removeAttribute('method');
    }
  });
}

function mailboxHtmlParts(html: string): { css: string; body: string } {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll(MAILBOX_DROP_TAGS).forEach(el => el.remove());
  const css = Array.from(doc.querySelectorAll('style')).map(el => el.textContent ?? '').join('\n');
  doc.querySelectorAll('style').forEach(el => el.remove());
  sanitizeMailboxTree(doc.body);
  return { css, body: doc.body.innerHTML };
}

function observaciones(data?: BuzonItemData | null): string | null {
  if (!data) return null;
  return data.observacionesYSeguimiento || data.ObservacionesYSeguimiento || null;
}

function Avatar({ name, size = 32 }: { name?: string | null; size?: number }) {
  const initials = name
    ? name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?';
  const colors = ['#0ea5e9', '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  const idx = (name?.charCodeAt(0) ?? 0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: colors[idx], color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.32), fontWeight: 700,
    }}>
      {initials}
    </div>
  );
}

export const BuzonTributario = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const match = useMatch('/buzon-tributario/:id');
  const configMatch = useMatch('/buzon-tributario/configuracion');
  const configOpen = configMatch != null;
  const openId = useMemo(() => {
    const routeId = match?.params.id;
    if (!routeId || routeId === 'configuracion') return null;
    const n = Number(routeId);
    return Number.isFinite(n) ? n : null;
  }, [match]);

  const [items, setItems] = useState<BuzonItem[]>([]);
  const [fallos, setFallos] = useState<BuzonFallo[]>([]);
  const [fallosIndicadores, setFallosIndicadores] = useState({ total: 0, nuevos: 0, descartados: 0 });
  const [totalItems, setTotalItems] = useState(0);
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [retryingFallos, setRetryingFallos] = useState(false);
  const [preview, setPreview] = useState<{ itemId: number; att: BuzonAttachment } | null>(null);
  const vista = parseBuzonVista(searchParams.get('vista'), searchParams.get('view'));
  const rawView = searchParams.get('view');
  const view = vista === 'fallos'
    ? (rawView && isFallosView(rawView) ? rawView : VIEW_FALLOS_NUEVOS)
    : (rawView && !isFallosView(rawView) ? rawView : 'NUEVO');
  const fallosMode = vista === 'fallos';
  const page = toApiPage(parseBuzonPage(searchParams.get('page')));
  const empresaIdFilter = searchParams.get('empresaId');
  const empresaRfcFilter = empresaIdFilter ? null : searchParams.get('rfc');
  const rfcFilter = empresaRfcFilter?.trim() || '';
  const asuntoFilter = fallosMode ? '' : normalizeBuzonAsunto(searchParams.get('asunto'));
  const [rfcDraft, setRfcDraft] = useState(rfcFilter);
  const empresaChipLabel = searchParams.get('empresa')
    || (empresaIdFilter ? `Empresa #${empresaIdFilter}` : null);
  const [detailCache, setDetailCache] = useState<Record<number, BuzonItem>>({});
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const [indicadores, setIndicadores] = useState<Record<string, { cantidad: number; porcentaje: number }>>({});
  const [asuntoIndicadores, setAsuntoIndicadores] = useState<ConceptoIndicador[]>([]);
  const [asignadosAMi, setAsignadosAMi] = useState<{ cantidad: number; porcentaje: number }>({ cantidad: 0, porcentaje: 0 });
  const [sinAsignar, setSinAsignar] = useState<{ cantidad: number; porcentaje: number }>({ cantidad: 0, porcentaje: 0 });
  const [health, setHealth] = useState<BuzonHealth | null>(null);
  const [gruposById, setGruposById] = useState<Record<number, string>>({});
  const [usuariosById, setUsuariosById] = useState<Record<number, string>>({});
  const [usuariosObjById, setUsuariosObjById] = useState<Record<number, UsuarioSearchItem>>({});
  const gruposCache = useRef<Record<number, string>>({});
  const usuariosCache = useRef<Record<number, string>>({});
  const usuariosObjCache = useRef<Record<number, UsuarioSearchItem>>({});
  const topRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const visiblePages = useMemo(
    () => pagerPageIndexes(page, totalPages),
    [page, totalPages],
  );

  const viewParams = useCallback((v: string): string => {
    if (v === VIEW_ASIGNADOS_A_MI) return '&asignadoA=me';
    if (v === VIEW_SIN_ASIGNAR) return '&asignadoA=none&excluirEstado=CERRADO';
    if (v === VIEW_FALLOS_NUEVOS) return '&estado=NUEVO';
    if (v === VIEW_FALLOS_DESCARTADOS) return '&estado=DESCARTADO';
    if (ESTADOS.includes(v as EstadoBuzon)) return `&estado=${encodeURIComponent(v)}`;
    return '';
  }, []);

  const empresaParams = useCallback((): string => {
    if (empresaIdFilter) return `&empresaId=${encodeURIComponent(empresaIdFilter)}`;
    if (empresaRfcFilter) return `&rfc=${encodeURIComponent(empresaRfcFilter)}`;
    return '';
  }, [empresaIdFilter, empresaRfcFilter]);

  const fetchIndicadores = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/indicadores?cacheBuster=${Date.now()}${empresaParams()}`, {
        headers: { ...authHeader() },
      });
      if (!res.ok) return;
      const data: Indicadores = await res.json();
      const map: Record<string, { cantidad: number; porcentaje: number }> = {};
      (data.estados ?? []).forEach(row => {
        if (row.estado) {
          map[row.estado] = { cantidad: row.cantidad ?? 0, porcentaje: row.porcentaje ?? 0 };
        }
      });
      setIndicadores(map);
      setAsignadosAMi({
        cantidad: data.asignadosAMi?.cantidad ?? 0,
        porcentaje: data.asignadosAMi?.porcentaje ?? 0,
      });
      setSinAsignar({
        cantidad: data.sinAsignar?.cantidad ?? 0,
        porcentaje: data.sinAsignar?.porcentaje ?? 0,
      });
      setAsuntoIndicadores(
        (data.asuntos ?? []).filter(row => Boolean(row.codigo)).map(row => ({
          codigo: String(row.codigo).toUpperCase(),
          nombre: row.nombre || row.codigo,
          cantidad: row.cantidad ?? 0,
          porcentaje: row.porcentaje ?? 0,
        })),
      );
    } catch {
      // keep previous counts
    }
  }, [empresaParams]);

  const fetchFallosIndicadores = useCallback(async () => {
    try {
      const res = await fetch(`${FALLOS_API}/indicadores?cacheBuster=${Date.now()}`, {
        headers: { ...authHeader() },
      });
      if (!res.ok) return;
      const data = await res.json();
      setFallosIndicadores({
        total: Number(data.total) || 0,
        nuevos: Number(data.nuevos) || 0,
        descartados: Number(data.descartados) || 0,
      });
    } catch {
      // keep previous counts
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${HEALTH_API}?cacheBuster=${Date.now()}`, {
          headers: { ...authHeader() },
        });
        if (!res.ok) throw new Error('No se pudo consultar el estado');
        const data: BuzonHealth = await res.json();
        if (!cancelled) setHealth(data);
      } catch (e) {
        if (!cancelled) {
          setHealth({
            ok: false,
            error: e instanceof Error ? e.message : 'No se pudo consultar el estado',
          });
        }
      }
    };
    load();
    const timer = window.setInterval(load, HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const loadUsuarioNombre = useCallback(async (id: number) => {
    if (usuariosCache.current[id] != null) {
      setUsuariosById({ ...usuariosCache.current });
      setUsuariosObjById({ ...usuariosObjCache.current });
      return;
    }
    try {
      const res = await fetch(`/api/usuarios/${id}`, { headers: { ...authHeader() } });
      if (!res.ok) {
        usuariosCache.current[id] = `#${id}`;
        usuariosObjCache.current[id] = { id };
      } else {
        const u: UsuarioSearchItem = await res.json();
        usuariosCache.current[id] = usuarioDisplayName(u);
        usuariosObjCache.current[id] = u;
      }
    } catch {
      usuariosCache.current[id] = `#${id}`;
      usuariosObjCache.current[id] = { id };
    }
    setUsuariosById({ ...usuariosCache.current });
    setUsuariosObjById({ ...usuariosObjCache.current });
  }, []);

  const loadGrupos = useCallback(async (rows: BuzonItem[]) => {
    const missing = Array.from(new Set(rows.map(r => r.grupoId).filter((id): id is number => id != null)))
      .filter(id => gruposCache.current[id] == null);
    if (missing.length === 0) {
      setGruposById({ ...gruposCache.current });
      return;
    }
    await Promise.all(missing.map(async id => {
      try {
        const res = await fetch(`/api/grupo-empresarials/${id}`, { headers: { ...authHeader() } });
        if (!res.ok) {
          gruposCache.current[id] = '';
          return;
        }
        const g = await res.json();
        gruposCache.current[id] = grupoNombreFromPayload(g);
      } catch {
        gruposCache.current[id] = '';
      }
    }));
    setGruposById({ ...gruposCache.current });
  }, []);

  const loadUsuarios = useCallback(async (rows: BuzonItem[]) => {
    const missing = Array.from(new Set(
      rows.flatMap(r => [r.asignadoA, r.cerradoPor]).filter((id): id is number => id != null),
    )).filter(id => usuariosCache.current[id] == null);
    if (missing.length === 0) {
      setUsuariosById({ ...usuariosCache.current });
      return;
    }
    await Promise.all(missing.map(id => loadUsuarioNombre(id)));
  }, [loadUsuarioNombre]);

  const fetchPage = useCallback(async (p: number, forView?: string) => {
    const activeView = forView ?? view;
    setLoading(true);
    try {
      if (isFallosView(activeView)) {
        const rfcFilter = empresaRfcFilter ? `&rfc=${encodeURIComponent(empresaRfcFilter)}` : '';
        const res = await fetch(
          `${FALLOS_API}?page=${p}&size=${PAGE_SIZE}${sortQuery(sortField, sortDir)}${viewParams(activeView)}${rfcFilter}&cacheBuster=${Date.now()}`,
          { headers: { ...authHeader() } },
        );
        if (res.ok) {
          const total = parseInt(res.headers.get('x-total-count') ?? '0', 10);
          setTotalItems(total);
          const data: BuzonFallo[] = await res.json();
          setFallos(data);
          setItems([]);
        }
        return;
      }
      const asuntoParam = asuntoFilter ? `&asunto=${encodeURIComponent(asuntoFilter)}` : '';
      const res = await fetch(
        `${API_BASE}?page=${p}&size=${PAGE_SIZE}${sortQuery(sortField, sortDir)}${viewParams(activeView)}${empresaParams()}${asuntoParam}&cacheBuster=${Date.now()}`,
        { headers: { ...authHeader() } },
      );
      if (res.ok) {
        const total = parseInt(res.headers.get('x-total-count') ?? '0', 10);
        setTotalItems(total);
        const data: BuzonItem[] = await res.json();
        setItems(data);
        setFallos([]);
        void loadGrupos(data);
        void loadUsuarios(data);
      }
    } finally {
      setLoading(false);
    }
  }, [view, viewParams, empresaParams, empresaRfcFilter, asuntoFilter, loadGrupos, loadUsuarios, sortField, sortDir]);

  useEffect(() => {
    fetchPage(page);
    fetchIndicadores();
    fetchFallosIndicadores();
    setSelected(new Set());
    topRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page, fetchPage, fetchIndicadores, fetchFallosIndicadores]);

  useEffect(() => {
    setRfcDraft(rfcFilter);
  }, [rfcFilter]);

  useEffect(() => {
    if (fallosMode) {
      setSortField('creado');
      setSortDir('desc');
    } else {
      setSortField('id');
      setSortDir('desc');
    }
  }, [fallosMode]);

  const goToPage = (next: number | ((current: number) => number)) => {
    const resolved = typeof next === 'function' ? next(page) : next;
    const oneBased = Math.max(1, resolved + 1);
    const params = new URLSearchParams(searchParams);
    params.set('page', String(oneBased));
    setSearchParams(params);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(dir => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'fecha' || field === 'creado' ? 'desc' : 'asc');
    }
    goToPage(0);
  };

  const applyRfcFilter = (value: string) => {
    const rfc = normalizeBuzonRfc(value);
    const next = new URLSearchParams(searchParams);
    if (rfc) {
      next.set('rfc', rfc);
      next.delete('empresaId');
      next.delete('empresa');
    } else {
      next.delete('rfc');
    }
    next.set('page', '1');
    setRfcDraft(rfc);
    setSearchParams(next);
  };

  const selectableIds = isFallosView(view) ? fallos.map(f => f.id) : items.map(i => i.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };

  const toggleOne = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const fetchDetail = useCallback(async (id: number) => {
    if (isFallosView(view)) {
      setDetailLoading(id);
      try {
        const res = await fetch(`${FALLOS_API}/${id}`, { headers: { ...authHeader() } });
        if (res.ok) {
          const data: BuzonFallo = await res.json();
          setFallos(prev => prev.map(i => i.id === id ? { ...i, ...data } : i));
          fetchFallosIndicadores();
        }
      } finally {
        setDetailLoading(null);
      }
      return;
    }
    if (detailCache[id]) return;
    setDetailLoading(id);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: { ...authHeader() } });
      if (res.ok) {
        const data: BuzonItem = await res.json();
        setDetailCache(prev => ({ ...prev, [id]: data }));
        setItems(prev => prev.map(i => i.id === id ? { ...i, ...data } : i));
        fetchIndicadores();
      }
    } finally {
      setDetailLoading(null);
    }
  }, [detailCache, fetchIndicadores, fetchFallosIndicadores, view]);

  useEffect(() => {
    if (openId != null) fetchDetail(openId);
  }, [openId, fetchDetail]);

  const listPath = buzonPath(searchParams, view);

  const handleOpen = (id: number) => {
    navigate(buzonPath(searchParams, view, { id }));
  };

  const handleCloseModal = () => {
    setPreview(null);
    navigate(listPath);
  };

  const descartarFallo = async (id: number) => {
    const res = await fetch(`${FALLOS_API}/${id}/descartar`, {
      method: 'PUT',
      headers: { ...authHeader() },
    });
    if (!res.ok) throw new Error('descartar failed');
    const data: BuzonFallo = await res.json();
    fetchFallosIndicadores();
    if (view === VIEW_FALLOS_DESCARTADOS) {
      setFallos(prev => prev.map(f => (f.id === id ? { ...f, ...data } : f)));
      return;
    }
    setFallos(prev => prev.filter(f => f.id !== id));
    setTotalItems(n => Math.max(0, n - 1));
    handleCloseModal();
  };

  const recuperarFallo = async (id: number) => {
    const res = await fetch(`${FALLOS_API}/${id}/recuperar`, {
      method: 'PUT',
      headers: { ...authHeader() },
    });
    if (!res.ok) throw new Error('recuperar failed');
    fetchFallosIndicadores();
    if (view === VIEW_FALLOS_DESCARTADOS) {
      setFallos(prev => prev.filter(f => f.id !== id));
      setTotalItems(n => Math.max(0, n - 1));
      handleCloseModal();
      return;
    }
    const data: BuzonFallo = await res.json();
    setFallos(prev => prev.map(f => (f.id === id ? { ...f, ...data } : f)));
  };

  const mergeItem = (id: number, data: BuzonItem) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...data } : i));
    setDetailCache(prev => ({
      ...prev,
      [id]: { ...(prev[id] ?? data), ...data, attachments: data.attachments ?? prev[id]?.attachments },
    }));
  };

  const asignarItem = async (id: number, user: UsuarioSearchItem) => {
    const nombre = usuarioDisplayName(user);
    const res = await fetch(`${API_BASE}/${id}/asignar?nombre=${encodeURIComponent(nombre)}`, {
      method: 'POST',
      headers: { ...authHeader() },
    });
    if (!res.ok) throw new Error('asignar failed');
    const data: BuzonItem = await res.json();
    usuariosCache.current[user.id] = nombre;
    if (data.asignadoA != null) usuariosCache.current[data.asignadoA] = nombre;
    setUsuariosById({ ...usuariosCache.current });
    mergeItem(id, data);
    fetchIndicadores();
  };

  const desasignarItem = async (id: number) => {
    const res = await fetch(`${API_BASE}/${id}/desasignar`, {
      method: 'POST',
      headers: { ...authHeader() },
    });
    if (!res.ok) throw new Error('desasignar failed');
    const data: BuzonItem = await res.json();
    mergeItem(id, { ...data, asignadoA: null });
    fetchIndicadores();
  };

  const cerrarItems = async (ids: number[]) => {
    const results = await Promise.allSettled(
      ids.map(async id => {
        const res = await fetch(`${API_BASE}/${id}/cerrar`, {
          method: 'POST',
          headers: { ...authHeader() },
        });
        if (!res.ok) throw new Error('cerrar failed');
        return res.json() as Promise<BuzonItem>;
      }),
    );
    const updated = new Map<number, BuzonItem>();
    results.forEach((result, idx) => {
      if (result.status === 'fulfilled') updated.set(ids[idx], result.value);
    });
    if (updated.size === 0) return;
    setItems(prev => prev.map(i => updated.get(i.id) ? { ...i, ...updated.get(i.id)! } : i));
    setDetailCache(prev => {
      const next = { ...prev };
      updated.forEach((item, id) => { next[id] = { ...(next[id] ?? item), ...item }; });
      return next;
    });
    setSelected(new Set());
    fetchIndicadores();
    fetchPage(page);
  };

  const retryFallos = async (ids: number[]) => {
    if (ids.length === 0 || retryingFallos) return;
    setRetryingFallos(true);
    try {
      const res = await fetch(`${FALLOS_API}/retry`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error('retry failed');
      const data = await res.json() as { deletedIds?: number[]; errors?: { id: number; error: string }[] };
      const deleted = new Set(data.deletedIds ?? []);
      setFallos(prev => prev.filter(f => !deleted.has(f.id)));
      setTotalItems(n => Math.max(0, n - deleted.size));
      setSelected(new Set());
      fetchFallosIndicadores();
      if ((data.errors ?? []).length > 0) {
        window.alert(`Reintentados: ${deleted.size}. No se pudieron reintentar: ${data.errors!.length}`);
      }
    } catch {
      window.alert('No se pudo reintentar los fallos.');
    } finally {
      setRetryingFallos(false);
    }
  };

  const downloadAttachment = async (itemId: number, attachment: BuzonAttachment) => {
    try {
      const blob = await fetchAttachmentBlob(itemId, attachment);
      const file = new Blob([blob], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.nombre || 'adjunto';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      window.alert('No se pudo descargar el adjunto.');
    }
  };

  const previewArchivo: IProfaseArchivo | null = preview
    ? { nombre: preview.att.nombre, path: preview.att.path || preview.att.nombre, esDirectorio: false }
    : null;
  const previewKind = preview ? attachmentPreviewKind(preview.att) : null;
  const previewFetchContent = useMemo(() => {
    if (!preview) return undefined;
    const itemId = preview.itemId;
    const att = preview.att;
    return () => fetchAttachmentBlob(itemId, att);
  }, [preview]);

  const selectedIds = Array.from(selected);
  const openedFallo = isFallosView(view) && openId != null
    ? (fallos.find(f => f.id === openId) ?? null)
    : null;
  const openedItem = isFallosView(view) || openId == null
    ? null
    : (detailCache[openId] ?? items.find(i => i.id === openId) ?? null);
  const openedGrupoNombre = openedItem?.grupoId == null
    ? 'Sin grupo'
    : (gruposById[openedItem.grupoId] || '');
  const openedAsignadoNombre = openedItem?.asignadoA == null
    ? ''
    : (usuariosById[openedItem.asignadoA] || '');
  const openedCerradoPorUsuario = openedItem?.cerradoPor == null
    ? null
    : (usuariosObjById[openedItem.cerradoPor] ?? {
      id: openedItem.cerradoPor,
      nombreCompleto: usuariosById[openedItem.cerradoPor] || undefined,
    });

  useEffect(() => {
    if (openedItem?.asignadoA != null) void loadUsuarioNombre(openedItem.asignadoA);
    if (openedItem?.cerradoPor != null) void loadUsuarioNombre(openedItem.cerradoPor);
  }, [openedItem?.asignadoA, openedItem?.cerradoPor, loadUsuarioNombre]);

  const handleViewChange = (v: string) => {
    setPreview(null);
    setSelected(new Set());
    const next = new URLSearchParams(searchParams);
    next.set('page', '1');
    navigate(buzonPath(next, v, { page: 1, vista }));
  };

  const handleVistaChange = (nextVista: BuzonVista) => {
    setPreview(null);
    setSelected(new Set());
    const next = new URLSearchParams(searchParams);
    next.set('page', '1');
    if (nextVista === 'fallos') {
      next.set('vista', 'fallos');
    } else {
      next.delete('vista');
    }
    const nextView = nextVista === 'fallos' ? VIEW_FALLOS_NUEVOS : 'NUEVO';
    navigate(buzonPath(next, nextView, { page: 1, vista: nextVista }));
  };

  const clearEmpresaFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('empresaId');
    next.delete('empresa');
    next.set('page', '1');
    setSearchParams(next);
  };

  const clearRfcFilter = () => {
    applyRfcFilter('');
  };

  const clearViewFilter = () => {
    handleViewChange(fallosMode ? VIEW_FALLOS_NUEVOS : 'inbox');
  };

  const handleAsuntoChange = (codigo: string) => {
    setPreview(null);
    setSelected(new Set());
    const next = new URLSearchParams(searchParams);
    const normalized = normalizeBuzonAsunto(codigo);
    if (normalized) next.set('asunto', normalized);
    else next.delete('asunto');
    next.set('page', '1');
    const nextView = normalized && !fallosMode && view === 'NUEVO' ? 'inbox' : view;
    navigate(buzonPath(next, nextView, { page: 1, vista }));
  };

  const clearAsuntoFilter = () => {
    handleAsuntoChange('');
  };

  const asuntoChipLabel = asuntoIndicadores.find(row => row.codigo === asuntoFilter)?.nombre
    || asuntoFilter;

  const showEmpresaChip = Boolean((empresaIdFilter || searchParams.get('empresa')) && empresaChipLabel);
  const showRfcChip = Boolean(rfcFilter);
  const showViewChip = fallosMode ? view !== VIEW_FALLOS_NUEVOS : view !== 'inbox';
  const showAsuntoChip = Boolean(asuntoFilter);

  return (
    <div style={s.root}>
      <style>{`
        @keyframes buzonSlideIn {
          0%   { opacity: 0; transform: translateY(-12px); background: #e0e7ff; }
          30%  { opacity: 1; transform: translateY(0);     background: #e0e7ff; }
          100% { background: transparent; }
        }
        @keyframes buzonSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes buzonLoadingBar {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div style={s.vistaToolbar}>
        <BuzonVistaToggle value={vista} onChange={handleVistaChange} />
        <div style={s.vistaToolbarRight}>
          <button
            type="button"
            title="Configuración"
            aria-label="Configuración"
            style={s.configGear}
            onClick={() => {
              const qs = searchParams.toString();
              navigate(qs ? `/buzon-tributario/configuracion?${qs}` : '/buzon-tributario/configuracion');
            }}
          >
            <FaCog size={16} />
            <span style={s.configGearLabel}>Configuración</span>
          </button>
          <BuzonHealthIndicator health={health} onHealthChange={setHealth} />
        </div>
      </div>

      <div style={s.indicadoresBar}>
        {!fallosMode && (
          <div className="procesos-estado-resumen" role="status" aria-live="polite" aria-label="Indicadores del buzón">
            <BuzonResumenMetric
              label="Asignados a mi"
              cantidad={asignadosAMi.cantidad}
              porcentaje={asignadosAMi.porcentaje}
              onClick={() => handleViewChange(VIEW_ASIGNADOS_A_MI)}
            />
            {ESTADOS.map(estado => {
              const row = indicadores[estado];
              return (
                <BuzonResumenMetric
                  key={estado}
                  label={estadoFiltroLabel(estado)}
                  cantidad={row?.cantidad ?? 0}
                  porcentaje={row?.porcentaje ?? 0}
                  onClick={() => handleViewChange(estado)}
                />
              );
            })}
            <BuzonResumenMetric
              label="Sin asignar"
              cantidad={sinAsignar.cantidad}
              porcentaje={sinAsignar.porcentaje}
              labelClass="procesos-estado-resumen__estado-label procesos-estado-resumen__estado-label--red"
              valueClass="procesos-resumen__value--red"
              onClick={() => handleViewChange(VIEW_SIN_ASIGNAR)}
            />
            <button
              type="button"
              className="procesos-estado-resumen__total procesos-estado-resumen__estado-filter"
              onClick={() => handleViewChange('inbox')}
              title="Filtrar por: Total"
              aria-label="Filtrar por Total"
            >
              <span className="procesos-estado-resumen__total-label">Total</span>
              <span className="procesos-estado-resumen__valor procesos-estado-resumen__valor--total">
                {ESTADOS.reduce((sum, estado) => sum + (indicadores[estado]?.cantidad ?? 0), 0)}
              </span>
            </button>
          </div>
        )}
        {!fallosMode && asuntoIndicadores.length > 0 && (
          <div className="procesos-estado-resumen" role="status" aria-live="polite" aria-label="Indicadores por asunto">
            {asuntoIndicadores.map(row => (
              <BuzonResumenMetric
                key={row.codigo}
                label={row.nombre || row.codigo || ''}
                cantidad={row.cantidad}
                porcentaje={row.porcentaje ?? 0}
                onClick={() => handleAsuntoChange(row.codigo || '')}
              />
            ))}
          </div>
        )}
        {fallosMode && (
          <div className="procesos-estado-resumen" role="status" aria-live="polite" aria-label="Indicadores de fallos">
            <BuzonResumenMetric
              label="Nuevos"
              cantidad={fallosIndicadores.nuevos}
              porcentaje={
                fallosIndicadores.total > 0
                  ? (fallosIndicadores.nuevos * 100) / fallosIndicadores.total
                  : null
              }
              labelClass="procesos-estado-resumen__estado-label procesos-estado-resumen__estado-label--red"
              valueClass="procesos-resumen__value--red"
              onClick={() => handleViewChange(VIEW_FALLOS_NUEVOS)}
            />
            <BuzonResumenMetric
              label="Descartados"
              cantidad={fallosIndicadores.descartados}
              porcentaje={
                fallosIndicadores.total + fallosIndicadores.descartados > 0
                  ? (fallosIndicadores.descartados * 100) /
                    (fallosIndicadores.total + fallosIndicadores.descartados)
                  : null
              }
              onClick={() => handleViewChange(VIEW_FALLOS_DESCARTADOS)}
            />
            <button
              type="button"
              className="procesos-estado-resumen__total procesos-estado-resumen__estado-filter"
              onClick={() => handleViewChange(VIEW_FALLOS)}
              title="Filtrar por: Total"
              aria-label="Filtrar por Total"
            >
              <span className="procesos-estado-resumen__total-label">Total</span>
              <span className="procesos-estado-resumen__valor procesos-estado-resumen__valor--total">
                {fallosIndicadores.total}
              </span>
            </button>
          </div>
        )}
      </div>

      {(showEmpresaChip || showRfcChip || showViewChip || showAsuntoChip) && (
        <div className="kanban-list-view__chips">
          {showAsuntoChip && (
            <span className="kanban-list-view__chip">
              <span>Asunto: {asuntoChipLabel}</span>
              <button
                type="button"
                className="kanban-list-view__chip-remove"
                onClick={clearAsuntoFilter}
                aria-label={`Quitar filtro Asunto: ${asuntoChipLabel}`}
              >
                ×
              </button>
            </span>
          )}
          {showViewChip && (
            <span className="kanban-list-view__chip">
              <span>{viewChipLabel(view)}</span>
              <button
                type="button"
                className="kanban-list-view__chip-remove"
                onClick={clearViewFilter}
                aria-label={`Quitar filtro ${viewChipLabel(view)}`}
              >
                ×
              </button>
            </span>
          )}
          {showEmpresaChip && (
            <span className="kanban-list-view__chip">
              <span>Empresa: {empresaChipLabel}</span>
              <button
                type="button"
                className="kanban-list-view__chip-remove"
                onClick={clearEmpresaFilter}
                aria-label="Quitar filtro de empresa"
              >
                ×
              </button>
            </span>
          )}
          {showRfcChip && (
            <span className="kanban-list-view__chip">
              <span>RFC: {rfcFilter}</span>
              <button
                type="button"
                className="kanban-list-view__chip-remove"
                onClick={clearRfcFilter}
                aria-label="Quitar filtro de RFC"
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}

      <div style={s.body}>
      <main style={s.main}>
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <label style={s.checkWrapper}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                style={s.check}
              />
              <FaChevronDown style={{ fontSize: 10, color: 'var(--crm-text-secondary)', marginLeft: 2 }} />
            </label>

            <ToolBtn title="Actualizar" onClick={() => { fetchPage(page); fetchIndicadores(); fetchFallosIndicadores(); }}>
              <FaSync style={{ fontSize: 14, color: loading ? '#6366f1' : 'var(--crm-text-secondary)' }} />
            </ToolBtn>

            {selectedIds.length > 0 && !isFallosView(view) && (
              <button style={s.cerrarBtn} onClick={() => cerrarItems(selectedIds)}>
                <FaCheckCircle style={{ fontSize: 11, marginRight: 6 }} />
                Cerrar
              </button>
            )}
            {selectedIds.length > 0 && isFallosView(view) && (
              <button
                style={s.cerrarBtn}
                onClick={() => void retryFallos(selectedIds)}
                disabled={retryingFallos}
              >
                <FaSync style={{ fontSize: 11, marginRight: 6 }} />
                {retryingFallos ? 'Reintentando…' : 'Reintentar'}
              </button>
            )}
          </div>

          <div style={s.toolbarRight}>
            <span style={s.pageInfo}>
              {totalItems === 0 ? '0' : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, totalItems)}`} de {totalItems}
            </span>
            <ToolBtn title="Página anterior" onClick={() => goToPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <FaChevronLeft style={{ fontSize: 13, color: page === 0 ? 'var(--crm-text-muted)' : 'var(--crm-text-secondary)' }} />
            </ToolBtn>
            <ToolBtn title="Página siguiente" onClick={() => goToPage(p => p + 1)} disabled={page >= totalPages - 1}>
              <FaChevronRight style={{ fontSize: 13, color: page >= totalPages - 1 ? 'var(--crm-text-muted)' : 'var(--crm-text-secondary)' }} />
            </ToolBtn>
          </div>
        </div>

        <div style={s.divider} />

        {loading && <div style={s.loadingBar} />}

        <div style={s.list} ref={topRef}>
          <div style={s.headerRow}>
            <div style={s.rowLeft} />
            <div style={s.rowContent}>
              <div style={s.rowMain}>
                {isFallosView(view) ? (
                  <>
                    <RfcFilterTh
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                      value={rfcDraft}
                      onChange={setRfcDraft}
                      onSubmit={() => applyRfcFilter(rfcDraft)}
                    />
                    <SortTh label="Asunto" field="subject" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.mailText} />
                    <SortTh label="Estado" field="estado" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.estadoCol} />
                    <SortTh label="Fecha" field="creado" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.dateCol} />
                  </>
                ) : (
                  <>
                    <SortTh label="Grupo" field="grupoId" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.grupoCol} />
                    <RfcFilterTh
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                      value={rfcDraft}
                      onChange={setRfcDraft}
                      onSubmit={() => applyRfcFilter(rfcDraft)}
                    />
                    <SortTh label="Asignado" field="asignadoA" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.asignadoCol} />
                    <SortTh label="Asunto" field="asunto" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.mailText} />
                    <SortTh label="Cerrado por" field="cerradoPor" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.cerradoCol} />
                    <SortTh label="Estado" field="estado" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.estadoCol} />
                    <SortTh label="Fecha" field="fecha" sortField={sortField} sortDir={sortDir} onSort={handleSort} style={s.dateCol} />
                  </>
                )}
              </div>
            </div>
            <div style={s.headerActionSpacer} />
          </div>
          {isFallosView(view) && fallos.length === 0 && !loading && (
            <div style={s.empty}>
              <FaInbox style={{ fontSize: 48, color: 'var(--crm-text-muted)', marginBottom: 12 }} />
              <span style={{ color: 'var(--crm-text-secondary)', fontSize: 15 }}>No hay fallos en el buzón tributario</span>
            </div>
          )}

          {!isFallosView(view) && items.length === 0 && !loading && (
            <div style={s.empty}>
              <FaInbox style={{ fontSize: 48, color: 'var(--crm-text-muted)', marginBottom: 12 }} />
              <span style={{ color: 'var(--crm-text-secondary)', fontSize: 15 }}>No hay mensajes en el buzón tributario</span>
            </div>
          )}

          {isFallosView(view) && fallos.map(fallo => {
            const unread = fallo.estado === 'NUEVO';
            const colors = falloEstadoColors(fallo.estado);
            return (
              <div
                key={fallo.id}
                style={s.row(!unread, selected.has(fallo.id))}
              >
                <div style={s.rowLeft} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(fallo.id)}
                    onChange={() => toggleOne(fallo.id)}
                    style={s.check}
                  />
                </div>
                <div style={s.rowContent} onClick={() => handleOpen(fallo.id)}>
                  <div style={s.rowMain}>
                    <div style={s.identity}>
                      <div style={s.identityMain}>
                        <Avatar name={fallo.rfc || 'Fallo'} />
                        <div style={s.identityText}>
                          <span style={s.sender(unread)}>{fallo.rfc || 'Sin RFC'}</span>
                        </div>
                      </div>
                    </div>
                    <div style={s.mailText}>
                      <span style={s.subject(unread)}>{fallo.subject || '(sin asunto)'}</span>
                      <span style={s.snippet}>{fallo.motivoError}</span>
                    </div>
                    <span style={s.estadoCol}>
                      <span style={s.typeBadge(colors.bg, colors.color)}>{falloEstadoLabel(fallo.estado)}</span>
                    </span>
                    <span style={{ ...s.dateCol, ...s.date(unread) }}>{fmtInstantFechaHora(fallo.creado)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  style={s.abrirBtn}
                  onClick={e => { e.stopPropagation(); handleOpen(fallo.id); }}
                >
                  <FaExternalLinkAlt style={{ fontSize: 10, marginRight: 6 }} />
                  Abrir
                </button>
              </div>
            );
          })}

          {!isFallosView(view) && items.map(item => {
            const unread = isNuevo(item.estado);
            const colors = estadoBadgeColors(item.estado);
            const nombreEmpresa = empresaNombre(item);
            return (
              <div
                key={item.id}
                style={s.row(!unread, selected.has(item.id))}
              >
                <div style={s.rowLeft} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggleOne(item.id)}
                    style={s.check}
                  />
                </div>

                <div style={s.rowContent} onClick={() => handleOpen(item.id)}>
                  <div style={s.rowMain}>
                    <div style={s.grupoCol}>
                      <span style={s.grupoName(unread)}>
                        {item.grupoId != null ? (gruposById[item.grupoId] || '…') : 'Sin grupo'}
                      </span>
                    </div>
                    <div style={s.identity}>
                      <div style={s.identityMain}>
                        <Avatar name={item.rfc} />
                        <div style={s.identityText}>
                          <span style={s.sender(unread)}>{item.rfc || 'Sin RFC'}</span>
                          {nombreEmpresa ? (
                            <span style={s.grupoLine}>{nombreEmpresa}</span>
                          ) : null}
                          {item.empresaId == null && <EmpresaNoRegistradaWarning />}
                        </div>
                      </div>
                    </div>
                    <div style={s.asignadoCol}>
                      <span style={s.asignadoName(unread, item.asignadoA == null)}>
                        {item.asignadoA == null
                          ? 'Sin asignar'
                          : (usuariosById[item.asignadoA] || '…')}
                      </span>
                    </div>
                    <div style={s.mailText}>
                      <span style={s.subject(unread)}>{buzonAsuntoLabel(item.asunto) || '(sin asunto)'}</span>
                      {buzonConceptoLabel(item.concepto) && (
                        <span style={s.snippet}>{buzonConceptoLabel(item.concepto)}</span>
                      )}
                    </div>
                    <div style={s.cerradoCol}>
                      {item.cerradoPor != null && (
                        <>
                          <span style={s.cerradoPor}>
                            Cerrado por: {usuariosById[item.cerradoPor] || '…'}
                          </span>
                          {item.cerrado && (
                            <span style={s.grupoLine}>{fmtInstantFechaHora(item.cerrado)}</span>
                          )}
                        </>
                      )}
                    </div>
                    <span style={s.estadoCol}>
                      <span style={s.typeBadge(colors.bg, colors.color)}>{estadoLabel(item.estado)}</span>
                    </span>
                    <span style={{ ...s.dateCol, ...s.date(unread) }}>{fmtFechaHora(item.fecha, item.hora)}</span>
                  </div>
                </div>

                <button
                  type="button"
                  style={s.abrirBtn}
                  onClick={e => { e.stopPropagation(); handleOpen(item.id); }}
                >
                  <FaExternalLinkAlt style={{ fontSize: 10, marginRight: 6 }} />
                  Abrir
                </button>
              </div>
            );
          })}
        </div>

        {totalItems > PAGE_SIZE && (
          <div style={s.bottomPager}>
            <span style={s.pageInfo}>
              Página {page + 1} de {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <PagerBtn label="« Primero" onClick={() => goToPage(0)} disabled={page === 0} />
              <PagerBtn label="‹ Anterior" onClick={() => goToPage(p => p - 1)} disabled={page === 0} />
              {visiblePages.map(i => (
                <PagerBtn key={i} label={String(i + 1)} onClick={() => goToPage(i)} active={i === page} />
              ))}
              <PagerBtn label="Siguiente ›" onClick={() => goToPage(p => p + 1)} disabled={page >= totalPages - 1} />
              <PagerBtn label="Último »" onClick={() => goToPage(totalPages - 1)} disabled={page >= totalPages - 1} />
            </div>
          </div>
        )}
      </main>
      </div>

      <BuzonFalloModal
        open={isFallosView(view) && openId !== null}
        fallo={openedFallo}
        loading={openId !== null && detailLoading === openId && openedFallo == null}
        onClose={handleCloseModal}
        onDescartar={descartarFallo}
        onRecuperar={recuperarFallo}
      />

      <BuzonMensajeModal
        open={!isFallosView(view) && openId !== null}
        item={openedItem}
        grupoNombre={openedGrupoNombre}
        asignadoNombre={openedAsignadoNombre}
        cerradoPorUsuario={openedCerradoPorUsuario}
        loading={openId !== null && detailLoading === openId && !detailCache[openId]}
        onClose={handleCloseModal}
        onDownload={downloadAttachment}
        onPreview={(itemId, att) => setPreview({ itemId, att })}
        onCerrar={id => cerrarItems([id])}
        onAsignar={asignarItem}
        onDesasignar={desasignarItem}
      />

      <BuzonTributarioUsuariosConfig
        open={configOpen}
        onClose={() => navigate(listPath)}
      />

      <PdfViewerModal
        open={preview != null && previewKind === 'pdf'}
        archivo={previewArchivo}
        fetchContent={previewFetchContent}
        zIndex={ATTACHMENT_VIEWER_Z}
        onClose={() => setPreview(null)}
      />
      <TxtViewerModal
        open={preview != null && previewKind === 'txt'}
        archivo={previewArchivo}
        fetchContent={previewFetchContent}
        zIndex={ATTACHMENT_VIEWER_Z}
        onClose={() => setPreview(null)}
      />
      <ImageViewerModal
        open={preview != null && previewKind === 'image'}
        archivo={previewArchivo}
        fetchContent={previewFetchContent}
        zIndex={ATTACHMENT_VIEWER_Z}
        onClose={() => setPreview(null)}
      />
      <ProfaseModal
        open={preview != null && previewKind === 'other'}
        titleId="buzon-adjunto-sin-preview-title"
        size="compact"
        zIndex={ATTACHMENT_VIEWER_Z}
      >
        <h2 id="buzon-adjunto-sin-preview-title" className="profase-modal-title">
          {preview?.att.nombre || 'Adjunto'}
        </h2>
        <p className="profase-modal-description">
          Este tipo de archivo no se puede previsualizar. Podés descargarlo para abrirlo.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={() => setPreview(null)}>
            Volver al mensaje
          </button>
          {preview && (
            <button
              type="button"
              className="profase-form-btn-secondary btn-sm"
              onClick={() => downloadAttachment(preview.itemId, preview.att)}
            >
              Descargar
            </button>
          )}
        </div>
      </ProfaseModal>
    </div>
  );
};

function BuzonFalloModal({
  open,
  fallo,
  loading,
  onClose,
  onDescartar,
  onRecuperar,
}: {
  open: boolean;
  fallo: BuzonFallo | null;
  loading: boolean;
  onClose: () => void;
  onDescartar: (id: number) => Promise<void>;
  onRecuperar: (id: number) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const descartado = fallo?.estado === 'DESCARTADO';

  const run = async (action: () => Promise<void>) => {
    if (fallo == null || busy) return;
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProfaseModal
      open={open}
      titleId="buzon-tributario-fallo-title"
      size="default"
    >
      <div style={modal.header}>
        <h2 id="buzon-tributario-fallo-title" className="profase-modal-title" style={modal.title}>
          {fallo?.subject || 'Fallo del buzón tributario'}
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {fallo && descartado && (
            <button
              type="button"
              className="profase-form-btn-primary btn-sm"
              onClick={() => void run(() => onRecuperar(fallo.id))}
              disabled={busy}
            >
              {busy ? 'Recuperando…' : 'Recuperar'}
            </button>
          )}
          {fallo && !descartado && (
            <button
              type="button"
              className="profase-form-btn-secondary btn-sm"
              onClick={() => void run(() => onDescartar(fallo.id))}
              disabled={busy}
            >
              {busy ? 'Descartando…' : 'Descartar'}
            </button>
          )}
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
      {loading && !fallo && <p className="profase-modal-description">Cargando…</p>}
      {fallo && (
        <div style={modal.body}>
          <div style={md.meta}>
            <MetaChip label="RFC" value={fallo.rfc || '—'} />
            <MetaChip label="Estado" value={falloEstadoLabel(fallo.estado)} />
            <MetaChip label="Fecha" value={fmtInstantFechaHora(fallo.creado) || '—'} />
          </div>
          <div style={{ ...md.obs, marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: 'var(--crm-text-secondary)' }}>
              Motivo
            </div>
            <div>{fallo.motivoError}</div>
          </div>
          {fallo.persistentId && (
            <p className="profase-modal-description" style={{ marginTop: 12, wordBreak: 'break-all' }}>
              ID persistente: {fallo.persistentId}
            </p>
          )}
        </div>
      )}
    </ProfaseModal>
  );
}

function BuzonResumenMetric({
  label,
  cantidad,
  porcentaje,
  onClick,
  labelClass = 'procesos-estado-resumen__estado-label',
  valueClass,
}: {
  label: string;
  cantidad: number;
  porcentaje: number | null;
  onClick: () => void;
  labelClass?: string;
  valueClass?: string;
}) {
  const pct = porcentaje == null ? null : Math.round(porcentaje);
  return (
    <div className="procesos-estado-resumen__estado">
      <button
        type="button"
        className="procesos-estado-resumen__filter-btn"
        onClick={onClick}
        title={`Filtrar por: ${label}`}
        aria-label={`Filtrar por ${label}`}
      >
        <span className={labelClass}>{label}</span>
      </button>
      <div className="procesos-estado-resumen__metricas">
        <span className={valueClass ? `procesos-estado-resumen__valor ${valueClass}` : 'procesos-estado-resumen__valor'}>
          {cantidad}
        </span>
        <span className="procesos-estado-resumen__valor">
          {pct == null ? 'N/D' : `${pct}%`}
        </span>
      </div>
    </div>
  );
}

function BuzonHealthIndicator({
  health,
  onHealthChange,
}: {
  health: BuzonHealth | null;
  onHealthChange: (health: BuzonHealth | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const sync = healthSyncOf(health);
  const status = healthStatusOf(health);
  const consulta = fmtHace(sync.ultimaConsulta);

  return (
    <>
      <button
        type="button"
        style={{ ...s.healthToolbarBtn, borderLeft: `4px solid ${status.color}` }}
        onClick={() => setOpen(true)}
        title="Ver detalle del estado"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Estado del buzón: ${status.label}. Última consulta ${consulta}. Ver detalle`}
      >
        <span style={s.configGearLabel}>Estado</span>
        <span style={{ ...s.healthToolbarStatus, color: status.color }}>
          <span style={{ ...s.healthDot, background: status.color }} />
          {status.label}
        </span>
        <span style={s.healthToolbarConsulta}>Consulta {consulta}</span>
      </button>
      <BuzonHealthModal
        open={open}
        health={health}
        onClose={() => setOpen(false)}
        onHealthChange={onHealthChange}
      />
    </>
  );
}

function HealthRows({
  rows,
  actions,
}: {
  rows: { key?: string; label: string; value: string; raw?: unknown }[];
  actions?: Partial<Record<string, React.ReactNode>>;
}) {
  return (
    <dl style={s.healthRows}>
      {rows.map(row => (
        <React.Fragment key={row.key || row.label}>
          <dt style={s.healthDt}>{row.label}</dt>
          <dd style={s.healthDd}>
            <span style={s.healthValue}>
              {row.key === 'country' && isMexicoCountry(row.raw ?? row.value) && (
                <img src={flagMexico} alt="Bandera de México" style={s.healthFlag} />
              )}
              {row.value}
              {row.key && actions?.[row.key]}
            </span>
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function HealthTabLabel({ label, ok, tone }: { label: string; ok?: boolean; tone?: HealthDotTone }) {
  const resolved = tone ?? (ok == null ? undefined : ok ? 'ok' : 'error');
  const color = healthDotColor(resolved);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {color && <span style={{ ...s.healthDot, background: color }} />}
      {label}
    </span>
  );
}

function BuzonHealthModal({
  open,
  health,
  onClose,
  onHealthChange,
}: {
  open: boolean;
  health: BuzonHealth | null;
  onClose: () => void;
  onHealthChange: (health: BuzonHealth | null) => void;
}) {
  const [notice, setNotice] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState(false);
  const status = healthStatusOf(health);
  const sync = healthSyncOf(health);
  const sincronizar = health?.sincronizar ?? sync.sincronizar ?? false;
  const extra = health
    ? Object.entries(health).filter(([key]) => !HEALTH_KNOWN_KEYS.has(key))
    : [];

  useEffect(() => {
    if (!open) {
      setNotice(null);
      setSyncing(false);
      setToggling(false);
    }
  }, [open]);

  const sincronizarAhora = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await fetch(CONSULTAR_API, {
        method: 'POST',
        headers: { ...authHeader() },
      });
      setNotice(consultarNotice(res.status));
    } catch {
      setNotice({ type: 'error', text: 'No se pudo iniciar la sincronización' });
    } finally {
      setSyncing(false);
    }
  };

  const cambiarSincronizar = async (next: boolean) => {
    if (toggling) return;
    setToggling(true);
    try {
      const res = await fetch(SINCRONIZAR_API, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sincronizar: next }),
      });
      if (!res.ok) {
        setNotice({ type: 'error', text: 'No se pudo actualizar la sincronización automática' });
        return;
      }
      const data: { sincronizar?: boolean } = await res.json();
      const enabled = data.sincronizar ?? next;
      onHealthChange(
        health
          ? { ...health, sincronizar: enabled, sync: { ...health.sync, sincronizar: enabled } }
          : { sincronizar: enabled, sync: { sincronizar: enabled } },
      );
    } catch {
      setNotice({ type: 'error', text: 'No se pudo actualizar la sincronización automática' });
    } finally {
      setToggling(false);
    }
  };

  const syncButton = (
    <button
      type="button"
      className="profase-form-btn-primary btn-sm"
      onClick={sincronizarAhora}
      disabled={syncing}
    >
      {syncing ? 'Sincronizando…' : 'Sincronizar Ahora'}
    </button>
  );

  const syncRows = healthObjectRows(healthSyncOf(health)).filter(row => row.key !== 'sincronizar');

  return (
    <ProfaseModal
      open={open}
      titleId="buzon-tributario-health-title"
      size="wide"
      scroll
    >
      <div style={modal.header}>
        <h2 id="buzon-tributario-health-title" className="profase-modal-title" style={modal.title}>
          Estado del Buzón Tributario
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>
      <div style={modal.body}>
        {notice && (
          <div
            role={notice.type === 'error' ? 'alert' : 'status'}
            className={`profase-form-import-alert ${
              notice.type === 'error' ? 'profase-form-import-alert--error' : 'profase-form-import-alert--success'
            }`}
          >
            {notice.text}
          </div>
        )}
        <div style={s.healthModalStatus}>
          <span style={{ ...s.healthDot, width: 10, height: 10, background: status.color }} />
          <strong style={{ color: status.color }}>{status.label}</strong>
        </div>
        <TabPanelContainer defaultTab="general">
          <TabPanel id="general" label={<HealthTabLabel label="General" tone={healthStatusTone(status.label)} />}>
            <HealthRows
              rows={[
                { label: 'OK', value: health == null ? '—' : health.ok ? 'Sí' : 'No' },
                { label: 'Estado', value: status.label },
                { label: 'Servicio', value: health?.service || '—' },
                ...(health?.error ? [{ label: 'Error', value: health.error }] : []),
              ]}
            />
          </TabPanel>
          <TabPanel
            id="sync"
            label={
              <HealthTabLabel
                label="Sincronización"
                tone={isConsultaStale(sync.ultimaConsulta) ? 'warning' : 'ok'}
              />
            }
          >
            <label style={s.syncSwitchRow}>
              <input
                type="checkbox"
                role="switch"
                className="form-check-input"
                checked={sincronizar}
                disabled={toggling}
                onChange={e => void cambiarSincronizar(e.target.checked)}
                aria-label="Sincronización automática"
              />
              <span>
                Sincronización automática:{' '}
                <strong>{sincronizar ? 'habilitada' : 'inhabilitada'}</strong>
              </span>
            </label>
            <HealthRows
              rows={syncRows}
              actions={{ ultimaConsulta: syncButton }}
            />
          </TabPanel>
          <TabPanel id="outlook" label={<HealthTabLabel label="Outlook" ok={health?.outlook?.ok} />}>
            <HealthRows rows={healthObjectRows(health?.outlook ?? undefined)} />
          </TabPanel>
          <TabPanel id="backend" label={<HealthTabLabel label="Backend SAT" ok={health?.backend?.ok} />}>
            <HealthRows rows={healthObjectRows(health?.backend ?? undefined)} />
          </TabPanel>
          <TabPanel id="vpn" label={<HealthTabLabel label="VPN" ok={health?.vpn?.ok} />}>
            <HealthRows rows={healthObjectRows(health?.vpn ?? undefined)} />
          </TabPanel>
          <TabPanel id="captcha" label={<HealthTabLabel label="Captcha" ok={health?.captcha?.ok} />}>
            <HealthRows rows={healthObjectRows(health?.captcha ?? undefined)} />
          </TabPanel>
          <TabPanel id="crm" label={<HealthTabLabel label="Profase CRM" ok={health?.profase_crm?.configured} />}>
            <HealthRows rows={healthObjectRows(health?.profase_crm ?? undefined)} />
          </TabPanel>
          {extra.length > 0 && (
            <TabPanel id="otros" label="Otros">
              <HealthRows
                rows={extra.map(([key, value]) => ({
                  key,
                  label: healthFieldLabel(key),
                  value: healthFieldValue(key, value),
                  raw: value,
                }))}
              />
            </TabPanel>
          )}
        </TabPanelContainer>
      </div>
    </ProfaseModal>
  );
}

function BuzonMensajeModal({
  open,
  item,
  grupoNombre,
  asignadoNombre,
  cerradoPorUsuario,
  loading,
  onClose,
  onDownload,
  onPreview,
  onCerrar,
  onAsignar,
  onDesasignar,
}: {
  open: boolean;
  item: BuzonItem | null;
  grupoNombre: string;
  asignadoNombre: string;
  cerradoPorUsuario?: UsuarioSearchItem | null;
  loading: boolean;
  onClose: () => void;
  onDownload: (itemId: number, attachment: BuzonAttachment) => void;
  onPreview: (itemId: number, attachment: BuzonAttachment) => void;
  onCerrar: (id: number) => void;
  onAsignar: (id: number, user: UsuarioSearchItem) => Promise<void>;
  onDesasignar: (id: number) => Promise<void>;
}) {
  const title = item?.data?.subject || buzonAsuntoLabel(item?.asunto) || 'Mensaje';

  return (
    <ProfaseModal
      open={open}
      titleId="buzon-tributario-mensaje-title"
      size="xlarge"
      scroll
      dialogClassName="buzon-mensaje-modal"
      dialogStyle={{ maxHeight: 'none', overflow: 'visible' }}
    >
      <div style={modal.header}>
        <h2 id="buzon-tributario-mensaje-title" className="profase-modal-title" style={modal.title}>
          {title}
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>
      <div style={modal.body}>
        {loading && (
          <div style={s.detailLoading}>
            <div style={s.spinner} />
            <span style={{ fontSize: 13, color: 'var(--crm-text-secondary)' }}>Cargando mensaje…</span>
          </div>
        )}
        {!loading && item && (
          <BuzonDetailView
            item={item}
            grupoNombre={grupoNombre}
            asignadoNombre={asignadoNombre}
            cerradoPorUsuario={cerradoPorUsuario}
            onDownload={onDownload}
            onPreview={onPreview}
            onCerrar={() => onCerrar(item.id)}
            onAsignar={user => onAsignar(item.id, user)}
            onDesasignar={() => onDesasignar(item.id)}
          />
        )}
        {!loading && !item && (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--crm-text-secondary)' }}>(sin contenido)</p>
        )}
      </div>
    </ProfaseModal>
  );
}

function MessageHtmlFrame({ html, title }: { html: string; title: string }) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    const { css, body } = mailboxHtmlParts(html);
    const styleEl = document.createElement('style');
    styleEl.textContent = `${css}\n${MAILBOX_SHADOW_RESET}`;
    const wrap = document.createElement('div');
    wrap.className = 'buzon-html-root';
    wrap.innerHTML = body;
    shadow.replaceChildren(styleEl, wrap);
  }, [html]);

  return (
    <div
      ref={hostRef}
      title={title}
      aria-label={title}
      style={{
        width: '100%',
        border: '1px solid var(--crm-border)',
        borderRadius: 8,
        background: '#fff',
        colorScheme: 'light',
        overflow: 'visible',
        display: 'block',
        padding: 8,
      }}
    />
  );
}

function AsignarUsuarioRow({
  asignadoNombre,
  asignadoA,
  cerradoPorUsuario,
  cerrado,
  onAsignar,
  onDesasignar,
}: {
  asignadoNombre: string;
  asignadoA?: number | null;
  cerradoPorUsuario?: UsuarioSearchItem | null;
  cerrado?: string | null;
  onAsignar: (user: UsuarioSearchItem) => Promise<void>;
  onDesasignar: () => Promise<void>;
}) {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UsuarioSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!searching) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      abortRef.current?.abort();
      setResults([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      fetch(`/api/usuarios?query=${encodeURIComponent(q)}&page=0&size=20&sort=nombre,asc`, {
        headers: { ...authHeader() },
        signal: controller.signal,
      })
        .then(async res => {
          if (!res.ok) throw new Error('search failed');
          return parseUsuarios(await res.json());
        })
        .then(rows => {
          if (!controller.signal.aborted) setResults(rows);
        })
        .catch(() => {
          if (!controller.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);
  }, [query, searching]);

  const closeSearch = () => {
    setSearching(false);
    setQuery('');
    setResults([]);
  };

  const handleSelect = async (user: UsuarioSearchItem) => {
    setSaving(true);
    try {
      await onAsignar(user);
      closeSearch();
    } finally {
      setSaving(false);
    }
  };

  const handleDesasignar = async () => {
    setSaving(true);
    try {
      await onDesasignar();
      closeSearch();
    } finally {
      setSaving(false);
    }
  };

  const label = asignadoA == null
    ? 'Sin asignar'
    : (asignadoNombre || '…');

  return (
    <div style={md.assignRow}>
      <FaUser style={{ fontSize: 12, color: 'var(--crm-text-secondary)', flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={{ fontSize: 11, color: 'var(--crm-text-secondary)' }}>Asignado a</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--crm-text-primary)' }}>{label}</span>
      </div>
      {!searching ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className="profase-form-btn-secondary btn-sm"
            onClick={() => setSearching(true)}
          >
            Asignar
          </button>
          {asignadoA != null && (
            <button
              type="button"
              className="profase-form-btn-secondary btn-sm"
              onClick={() => { void handleDesasignar(); }}
              disabled={saving}
            >
              Desasignar
            </button>
          )}
          {cerradoPorUsuario && <CerradoPor usuario={cerradoPorUsuario} cerrado={cerrado} />}
        </div>
      ) : (
        <div style={md.assignSearch}>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar usuario…"
            autoComplete="off"
            autoFocus
            disabled={saving}
            aria-label="Buscar usuario"
            style={md.assignInput}
          />
          <button
            type="button"
            className="profase-form-btn-secondary btn-sm"
            onClick={closeSearch}
            disabled={saving}
          >
            Cancelar
          </button>
          {(loading || query.trim() || results.length > 0) && (
            <div style={md.assignResults}>
              {loading ? (
                <div style={md.assignEmpty}>Buscando…</div>
              ) : !query.trim() ? (
                <div style={md.assignEmpty}>Escriba para buscar</div>
              ) : results.length === 0 ? (
                <div style={md.assignEmpty}>Sin resultados</div>
              ) : results.map(user => (
                <button
                  key={user.id}
                  type="button"
                  disabled={saving}
                  style={md.assignResult}
                  onClick={() => { void handleSelect(user); }}
                >
                  <span style={{ fontWeight: 600 }}>{usuarioDisplayName(user)}</span>
                  {user.email && (
                    <span style={{ fontSize: 11, color: 'var(--crm-text-secondary)' }}>{user.email}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BuzonDetailView({
  item,
  grupoNombre,
  asignadoNombre,
  cerradoPorUsuario,
  onDownload,
  onPreview,
  onCerrar,
  onAsignar,
  onDesasignar,
}: {
  item: BuzonItem;
  grupoNombre: string;
  asignadoNombre: string;
  cerradoPorUsuario?: UsuarioSearchItem | null;
  onDownload: (itemId: number, attachment: BuzonAttachment) => void;
  onPreview: (itemId: number, attachment: BuzonAttachment) => void;
  onCerrar: () => void;
  onAsignar: (user: UsuarioSearchItem) => Promise<void>;
  onDesasignar: () => Promise<void>;
}) {
  const data = item.data;
  const body = data?.body ?? '';
  const obs = observaciones(data);
  const attachments = item.attachments ?? [];
  const colors = estadoBadgeColors(item.estado);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={md.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={item.rfc} size={36} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={md.fromName}>{item.rfc || 'Sin RFC'}</span>
            {empresaNombre(item) ? (
              <span style={md.fromEmail}>
                {empresaNombre(item)}{data?.correo ? ` · ${data.correo}` : ''}
              </span>
            ) : data?.correo ? (
              <span style={md.fromEmail}>{data.correo}</span>
            ) : null}
            {item.empresaId == null && <EmpresaNoRegistradaWarning />}
            <span style={md.fromEmail}>{grupoNombre || 'Sin grupo'}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={md.date}>{fmtFechaHora(item.fecha, item.hora)}</span>
          <span style={s.typeBadge(colors.bg, colors.color)}>{estadoLabel(item.estado)}</span>
        </div>
      </div>

      <div style={md.assignToolbar}>
        <AsignarUsuarioRow
          asignadoNombre={asignadoNombre}
          asignadoA={item.asignadoA}
          cerradoPorUsuario={item.estado === 'CERRADO' ? cerradoPorUsuario : null}
          cerrado={item.estado === 'CERRADO' ? item.cerrado : null}
          onAsignar={onAsignar}
          onDesasignar={onDesasignar}
        />
        {item.estado !== 'CERRADO' && (
          <button
            type="button"
            style={s.cerrarMsgBtn}
            onClick={e => { e.stopPropagation(); onCerrar(); }}
          >
            <FaCheckCircle style={{ fontSize: 11, marginRight: 6 }} />
            Cerrar mensaje
          </button>
        )}
      </div>

      <h2 style={md.subject}>{data?.subject || buzonAsuntoLabel(item.asunto)}</h2>

      <div style={md.meta}>
        {buzonConceptoLabel(item.concepto) && (
          <MetaChip label="Concepto" value={buzonConceptoLabel(item.concepto)} />
        )}
        {data?.folio && <MetaChip label="Folio" value={data.folio} />}
        {data?.fechaConsulta && <MetaChip label="Consulta" value={data.fechaConsulta} />}
      </div>

      <div style={md.divider} />

      {body
        ? looksLikeHtml(body)
          ? <MessageHtmlFrame title={`mensaje-${item.id}`} html={body} />
          : <p style={{ margin: 0, fontSize: 14, color: 'var(--crm-text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{body}</p>
        : <p style={{ margin: 0, fontSize: 14, color: 'var(--crm-text-secondary)' }}>(sin contenido)</p>
      }

      {obs && (
        <div style={md.obs}>
          <strong style={{ fontSize: 12, color: 'var(--crm-form-warning-text)' }}>Observaciones y seguimiento</strong>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--crm-text-primary)', whiteSpace: 'pre-wrap' }}>{obs}</p>
        </div>
      )}

      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <strong style={{ fontSize: 12, color: 'var(--crm-text-secondary)' }}>Adjuntos</strong>
          {attachments.map(att => (
            <div key={att.id} style={md.attRow}>
              <FaPaperclip style={{ fontSize: 12, flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {att.nombre}
              </span>
              <button
                type="button"
                title="Vista previa"
                aria-label={`Vista previa de ${att.nombre}`}
                style={md.attIconBtn}
                onClick={e => { e.stopPropagation(); onPreview(item.id, att); }}
              >
                <FaEye style={{ fontSize: 13 }} />
              </button>
              <button
                type="button"
                title="Descargar"
                aria-label={`Descargar ${att.nombre}`}
                style={md.attIconBtn}
                onClick={e => { e.stopPropagation(); onDownload(item.id, att); }}
              >
                <FaDownload style={{ fontSize: 12 }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetaChip({ label, value }: { label: string; value: string }) {
  return (
    <span style={md.chip}>
      <span style={{ color: 'var(--crm-text-secondary)' }}>{label}:</span> {value}
    </span>
  );
}

function ToolBtn({ children, title, onClick, disabled }: {
  children: React.ReactNode; title?: string; onClick?: () => void; disabled?: boolean;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        all: 'unset', cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: '50%',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLElement).style.background = 'var(--crm-surface-hover)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

function BuzonVistaToggle({
  value,
  onChange,
}: {
  value: BuzonVista;
  onChange: (vista: BuzonVista) => void;
}) {
  return (
    <div className="actividades-vista-toggle" role="group" aria-label="Vista">
      <div className="actividades-vista-toggle__options">
        <button
          type="button"
          className={`actividades-vista-toggle__btn${value === 'buzon' ? ' is-active is-buzon' : ''}`}
          aria-pressed={value === 'buzon'}
          onClick={() => onChange('buzon')}
        >
          Buzón Tributario
        </button>
        <button
          type="button"
          className={`actividades-vista-toggle__btn${value === 'fallos' ? ' is-active is-fallos' : ''}`}
          aria-pressed={value === 'fallos'}
          onClick={() => onChange('fallos')}
        >
          Fallos
        </button>
      </div>
    </div>
  );
}

function RfcFilterTh({
  sortField,
  sortDir,
  onSort,
  value,
  onChange,
  onSubmit,
}: {
  sortField: string;
  sortDir: SortDir;
  onSort: (field: string) => void;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div style={s.rfcFilterCol}>
      <SortTh label="RFC" field="rfc" sortField={sortField} sortDir={sortDir} onSort={onSort} />
      <input
        type="text"
        value={value}
        placeholder="Filtrar RFC"
        aria-label="Filtrar por RFC"
        maxLength={13}
        onChange={e => onChange(e.target.value.toUpperCase())}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
        onClick={e => e.stopPropagation()}
        style={s.rfcFilterInput}
      />
    </div>
  );
}

function SortTh({
  label, field, sortField, sortDir, onSort, style,
}: {
  label: string;
  field: string;
  sortField: string;
  sortDir: SortDir;
  onSort: (field: string) => void;
  style?: React.CSSProperties;
}) {
  const active = sortField === field;
  const Icon = !active ? FaSort : sortDir === 'asc' ? FaSortUp : FaSortDown;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      title={`Ordenar por ${label}`}
      style={{
        ...style,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: active ? 'var(--crm-text-primary)' : 'var(--crm-text-secondary)',
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: active ? 700 : 600,
        letterSpacing: 0.2,
        textTransform: 'uppercase',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <Icon style={{ fontSize: 11, flexShrink: 0, opacity: active ? 1 : 0.45 }} />
    </button>
  );
}

function PagerBtn({ label, onClick, disabled, active }: {
  label: string; onClick: () => void; disabled?: boolean; active?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '5px 10px', borderRadius: 6, fontSize: 13, fontWeight: active ? 700 : 400,
        border: active ? '1px solid var(--crm-accent-btn-border)' : '1px solid var(--crm-border)',
        background: active ? 'var(--crm-accent-btn-bg)' : 'var(--crm-surface)',
        color: active ? 'var(--crm-accent-btn-text)' : disabled ? 'var(--crm-text-muted)' : 'var(--crm-text-primary)',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

const modal: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 16, marginBottom: 8,
  },
  title: {
    margin: 0, flex: 1, fontSize: 18, fontWeight: 700, color: 'var(--crm-text-primary)', lineHeight: 1.3,
  },
  body: {
    paddingTop: 8,
  },
};

const md: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 },
  fromName: { fontSize: 14, fontWeight: 700, color: 'var(--crm-text-primary)' },
  fromEmail: { fontSize: 12, color: 'var(--crm-text-secondary)' },
  date: { fontSize: 12, color: 'var(--crm-text-secondary)', whiteSpace: 'nowrap' },
  subject: { margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--crm-text-primary)' },
  divider: { height: 1, background: 'var(--crm-border)' },
  meta: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: {
    display: 'inline-flex', gap: 4, alignItems: 'center',
    padding: '3px 8px', borderRadius: 8, background: 'var(--crm-surface-muted)',
    border: '1px solid var(--crm-border)', fontSize: 12, color: 'var(--crm-text-primary)',
  },
  obs: {
    padding: 12, borderRadius: 8, background: 'var(--crm-form-warning-bg)',
    border: '1px solid var(--crm-form-warning-border)',
  },
  attRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', borderRadius: 8,
    border: '1px solid var(--crm-border)', background: 'var(--crm-surface-muted)',
    fontSize: 13, color: 'var(--crm-text-primary)',
  },
  attIconBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 7, flexShrink: 0,
    border: '1px solid var(--crm-border)', background: 'var(--crm-surface)',
    color: 'var(--crm-text-primary)', cursor: 'pointer',
  },
  assignRow: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    flex: 1, minWidth: 0,
    padding: '10px 12px', borderRadius: 8,
    border: '1px solid var(--crm-border)', background: 'var(--crm-surface-muted)',
  },
  assignToolbar: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
  },
  assignSearch: {
    position: 'relative', display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 220,
  },
  assignInput: {
    flex: 1, minWidth: 160, height: 32, padding: '0 10px',
    borderRadius: 7, border: '1px solid var(--crm-border)',
    background: 'var(--crm-surface)', color: 'var(--crm-text-primary)', fontSize: 13,
  },
  assignResults: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, marginTop: 4,
    maxHeight: 220, overflowY: 'auto',
    border: '1px solid var(--crm-border)', borderRadius: 8,
    background: 'var(--crm-surface)', boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
  },
  assignEmpty: {
    padding: '10px 12px', fontSize: 12, color: 'var(--crm-text-secondary)',
  },
  assignResult: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
    width: '100%', padding: '8px 12px', border: 'none', background: 'transparent',
    color: 'var(--crm-text-primary)', fontSize: 13, cursor: 'pointer', textAlign: 'left',
  },
};

const s = {
  root: {
    display: 'flex', flexDirection: 'column' as const, height: '100%', minHeight: '80vh',
    fontFamily: 'Google Sans, Roboto, Arial, sans-serif',
    background: 'var(--crm-body-bg)',
    color: 'var(--crm-text-primary)',
  } as React.CSSProperties,

  indicadoresBar: {
    display: 'flex', flexDirection: 'column' as const, gap: 12,
    padding: '12px 16px 4px',
  } as React.CSSProperties,

  vistaToolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' as const,
    padding: '12px 16px 0',
  } as React.CSSProperties,

  vistaToolbarRight: {
    display: 'flex', alignItems: 'stretch', gap: 10,
  } as React.CSSProperties,

  configGear: {
    display: 'inline-flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', gap: 4,
    minWidth: 88, alignSelf: 'stretch',
    padding: '10px 12px', borderRadius: 10,
    border: '1px solid var(--crm-border)',
    background: 'var(--crm-surface)',
    color: 'var(--crm-text-secondary)',
    cursor: 'pointer',
  } as React.CSSProperties,

  configGearLabel: {
    fontSize: 11, fontWeight: 600, color: 'var(--crm-text-secondary)', lineHeight: 1.2,
  } as React.CSSProperties,

  healthToolbarBtn: {
    display: 'inline-flex', flexDirection: 'column' as const, alignItems: 'flex-start', justifyContent: 'center', gap: 2,
    minWidth: 128, alignSelf: 'stretch',
    padding: '8px 12px', borderRadius: 10,
    border: '1px solid var(--crm-border)',
    background: 'var(--crm-surface)',
    color: 'var(--crm-text-primary)',
    cursor: 'pointer',
    textAlign: 'left' as const,
    font: 'inherit',
  } as React.CSSProperties,

  healthToolbarStatus: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 14, fontWeight: 700, lineHeight: 1.2,
  } as React.CSSProperties,

  healthToolbarConsulta: {
    fontSize: 11, fontWeight: 600, color: 'var(--crm-text-secondary)', whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  healthDot: {
    width: 8, height: 8, borderRadius: 999, flex: '0 0 auto',
  } as React.CSSProperties,

  healthModalStatus: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    marginBottom: 12, fontSize: 16,
  } as React.CSSProperties,

  syncSwitchRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 14, fontSize: 13, color: 'var(--crm-text-primary)', cursor: 'pointer',
  } as React.CSSProperties,

  healthRows: {
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 38%) 1fr',
    gap: '6px 16px',
  } as React.CSSProperties,

  healthDt: {
    fontSize: 12, fontWeight: 600, color: 'var(--crm-text-secondary)',
  } as React.CSSProperties,

  healthDd: {
    margin: 0, fontSize: 13, color: 'var(--crm-text-primary)', wordBreak: 'break-word' as const,
  } as React.CSSProperties,

  healthValue: {
    display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  healthFlag: {
    height: 16, width: 'auto', display: 'block',
    border: '1px solid var(--crm-border)',
    borderRadius: 2,
  } as React.CSSProperties,

  body: {
    display: 'flex', flex: 1, minHeight: 0,
  } as React.CSSProperties,

  main: {
    flex: 1, background: 'var(--crm-surface)',
    borderRadius: 16, margin: '8px 16px',
    boxShadow: '0 1px 2px var(--crm-topbar-shadow)',
    display: 'flex', flexDirection: 'column' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  toolbar: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 16px',
    minHeight: 52,
  } as React.CSSProperties,

  toolbarLeft: {
    display: 'flex', alignItems: 'center', gap: 4,
  } as React.CSSProperties,

  toolbarRight: {
    display: 'flex', alignItems: 'center', gap: 4,
  } as React.CSSProperties,

  checkWrapper: {
    display: 'flex', alignItems: 'center', gap: 2,
    cursor: 'pointer', padding: '0 6px',
  } as React.CSSProperties,

  check: {
    width: 16, height: 16, accentColor: '#6366f1', cursor: 'pointer',
  } as React.CSSProperties,

  pageInfo: {
    fontSize: 13, color: 'var(--crm-text-secondary)', marginRight: 4,
  } as React.CSSProperties,

  divider: {
    height: 1, background: 'var(--crm-border)',
  } as React.CSSProperties,

  loadingBar: {
    height: 3,
    background: 'linear-gradient(90deg, #6366f1 0%, #818cf8 50%, #6366f1 100%)',
    backgroundSize: '200% 100%',
    animation: 'buzonLoadingBar 1.2s linear infinite',
  } as React.CSSProperties,

  list: {
    flex: 1, overflowY: 'auto' as const,
  },

  headerRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '8px 16px',
    background: 'var(--crm-surface-muted)',
    borderBottom: '1px solid var(--crm-border)',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
  } as React.CSSProperties,

  headerActionSpacer: {
    width: 80, flexShrink: 0,
  } as React.CSSProperties,

  estadoCol: {
    display: 'inline-flex', alignItems: 'center',
    width: 88, minWidth: 88, flexShrink: 0,
  } as React.CSSProperties,

  dateCol: {
    width: 130, minWidth: 130, flexShrink: 0,
  } as React.CSSProperties,

  empty: {
    display: 'flex', flexDirection: 'column' as const,
    alignItems: 'center', justifyContent: 'center',
    padding: '80px 24px',
  },

  row: (read: boolean, sel: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '8px 16px',
    background: sel
      ? 'var(--crm-accent-btn-bg)'
      : read
        ? 'var(--crm-surface)'
        : 'color-mix(in srgb, #3b82f6 16%, var(--crm-surface))',
    borderBottom: '1px solid var(--crm-border)',
    cursor: 'default',
    transition: 'background 0.1s',
  }),

  rowLeft: {
    display: 'flex', alignItems: 'center',
    width: 16, flexShrink: 0,
  } as React.CSSProperties,

  rowContent: {
    flex: 1, minWidth: 0, cursor: 'pointer',
  } as React.CSSProperties,

  rowMain: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    minWidth: 0,
  } as React.CSSProperties,

  grupoCol: {
    display: 'flex', flexDirection: 'column' as const, gap: 1,
    width: 160, flexShrink: 0, minWidth: 160,
  } as React.CSSProperties,

  grupoName: (unread: boolean): React.CSSProperties => ({
    fontSize: 14, fontWeight: unread ? 600 : 400,
    color: 'var(--crm-text-primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }),

  rfcFilterCol: {
    display: 'flex', flexDirection: 'column' as const, gap: 6,
    width: 360, flexShrink: 0, minWidth: 360,
  } as React.CSSProperties,

  rfcFilterInput: {
    width: '100%',
    boxSizing: 'border-box' as const,
    padding: '5px 8px',
    fontSize: 12,
    border: '1px solid var(--crm-border)',
    borderRadius: 6,
    background: 'var(--crm-input-bg, var(--crm-surface))',
    color: 'var(--crm-text-primary)',
    fontFamily: 'inherit',
  } as React.CSSProperties,

  identity: {
    display: 'flex', flexDirection: 'column' as const, gap: 1,
    width: 360, flexShrink: 0, minWidth: 360,
  } as React.CSSProperties,

  identityMain: {
    display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0,
  } as React.CSSProperties,

  identityText: {
    display: 'flex', flexDirection: 'column' as const, gap: 1, minWidth: 0,
  } as React.CSSProperties,

  empresaWarning: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 11, fontWeight: 600,
    color: 'var(--crm-form-warning-text)',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  asignadoCol: {
    display: 'flex', flexDirection: 'column' as const, gap: 1,
    width: 160, flexShrink: 0, minWidth: 160,
  } as React.CSSProperties,

  asignadoName: (unread: boolean, empty: boolean): React.CSSProperties => ({
    fontSize: 14, fontWeight: unread ? 600 : 400,
    color: empty ? 'var(--crm-text-muted)' : 'var(--crm-text-primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }),

  sender: (unread: boolean): React.CSSProperties => ({
    fontSize: 14, fontWeight: unread ? 700 : 500,
    color: 'var(--crm-text-primary)', whiteSpace: 'nowrap',
  }),

  grupoLine: {
    fontSize: 12, color: 'var(--crm-text-secondary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  mailText: {
    display: 'flex', flexDirection: 'column' as const, gap: 1,
    flex: 1, minWidth: 0,
  } as React.CSSProperties,

  cerradoCol: {
    display: 'flex', flexDirection: 'column' as const, gap: 1,
    width: 180, flexShrink: 0, minWidth: 180,
  } as React.CSSProperties,

  cerradoPor: {
    fontSize: 13, fontWeight: 500,
    color: 'var(--crm-text-primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  } as React.CSSProperties,

  subject: (unread: boolean): React.CSSProperties => ({
    fontSize: 14, fontWeight: unread ? 600 : 400,
    color: 'var(--crm-text-primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  }),

  snippet: {
    fontSize: 13, color: 'var(--crm-text-secondary)',
    overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  date: (unread: boolean): React.CSSProperties => ({
    fontSize: 12, fontWeight: unread ? 700 : 400,
    color: 'var(--crm-text-secondary)', whiteSpace: 'nowrap', flexShrink: 0, paddingTop: 2,
  }),

  typeBadge: (bg: string, color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '1px 7px', borderRadius: 10,
    fontSize: 10, fontWeight: 600,
    background: bg, color,
  }),

  abrirBtn: {
    display: 'inline-flex', alignItems: 'center', flexShrink: 0,
    padding: '6px 12px', background: '#6366f1', color: '#fff',
    border: 'none', borderRadius: 7,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  } as React.CSSProperties,

  detailLoading: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0',
  } as React.CSSProperties,

  spinner: {
    width: 18, height: 18, borderRadius: '50%',
    border: '2px solid var(--crm-border)',
    borderTopColor: '#6366f1',
    animation: 'buzonSpin 0.7s linear infinite',
    flexShrink: 0,
  } as React.CSSProperties,

  bottomPager: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
    padding: '10px 16px',
    borderTop: '1px solid var(--crm-border)',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  cerrarBtn: {
    display: 'inline-flex', alignItems: 'center',
    padding: '6px 12px',
    background: 'color-mix(in srgb, var(--crm-success-text) 16%, var(--crm-surface))',
    color: 'var(--crm-success-text)',
    border: '1px solid color-mix(in srgb, var(--crm-success-text) 35%, var(--crm-border))',
    borderRadius: 7,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    alignSelf: 'flex-start',
  } as React.CSSProperties,

  cerrarMsgBtn: {
    display: 'inline-flex', alignItems: 'center',
    padding: '6px 12px',
    background: 'color-mix(in srgb, var(--crm-success-text) 16%, var(--crm-surface))',
    color: 'var(--crm-success-text)',
    border: '1px solid color-mix(in srgb, var(--crm-success-text) 35%, var(--crm-border))',
    borderRadius: 7,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    marginLeft: 'auto',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
};

export default BuzonTributario;
